import { useState, useEffect } from 'react';
import { notification } from 'antd';
import { analyzeAndUpdateWorkflow } from '@/utils/analysisWorkflow';
import { WorkflowType } from 'src/enums/EWorkflow';
import ShortUniqueId from 'short-unique-id';
import { IUseCalendarBookingLogicProps, IWorkflow } from '@/interfaces/ICalendarWorkflows';

const uid = new ShortUniqueId({ length: 10 });

export const useCalendarBookingLogic = ({
	clientObject,
	mutation,
	refetch,
	selectedAgentId,
	dispatch,
	closeModal,
}: IUseCalendarBookingLogicProps) => {
	const [existingWorkflows, setExistingWorkflows] = useState<IWorkflow[]>([]);

	useEffect(() => {
		if (clientObject?.workflows) {
			try {
				const workflowData = JSON.parse(clientObject.workflows) || [];
				setExistingWorkflows(workflowData);
			} catch {
				notification.error({ message: 'Failed to parse existing workflows' });
				setExistingWorkflows([]);
			}
		} else {
			setExistingWorkflows([]);
		}
	}, [clientObject?.workflows]);

	const handleSubmit = async (
		values: any,
		isEditing: boolean,
		currentWorkflow: IWorkflow | null,
	) => {
		try {
			let allWorkflows = [...existingWorkflows];

			if (isEditing && currentWorkflow) {
				const updatedWorkflow = {
					...currentWorkflow,
					description: values.description,
					textMessage: values.textMessage,
					voiceResponse: values.voiceResponse || '',
					postExecutionPrompt: values.postExecutionPrompt,
					askForPermissionOnText: values.askForPermissionOnText,
					shortURLEnabled: values.shortURLEnabled === undefined ? true : values.shortURLEnabled,
					customTextFooter: values.customTextFooter || '',
				};

				allWorkflows = allWorkflows.map((workflow) =>
					workflow.id === currentWorkflow.id ? updatedWorkflow : workflow,
				);
			} else {
				const newScenario = {
					id: uid.rnd(),
					description: values.description,
					textMessage: values.textMessage,
					voiceResponse: values.voiceResponse || '',
					postExecutionPrompt: values.postExecutionPrompt,
					askForPermissionOnText: values.askForPermissionOnText,
					shortURLEnabled: values.shortURLEnabled === undefined ? true : values.shortURLEnabled,
					customTextFooter: values.customTextFooter || '',
					configEnabled: false,
					fallbackMessage: '',
					availability: null,
					bookingText: true,
				};

				allWorkflows = [...existingWorkflows, newScenario];
			}

			await mutation.mutateAsync({
				workflows: allWorkflows,
				agentId: selectedAgentId,
			});

			await refetch();

			notification.success({
				message: isEditing
					? 'Calendar booking workflow updated successfully!'
					: 'Calendar booking workflow added successfully!',
				duration: 3,
			});

			closeModal();

			await analyzeAndUpdateWorkflow(
				{ workflows: allWorkflows },
				WorkflowType.TEXT,
				dispatch,
				selectedAgentId,
			);
		} catch (err) {
			notification.error({ message: 'Something went wrong, please try again later!' });
			throw err;
		}
	};

	const getDefaultFormValues = () => ({
		description: 'Send a booking link when caller wants to schedule an appointment',
		textMessage: "Hi! Here's the link to book your appointment: [YOUR_BOOKING_LINK]",
		voiceResponse: "I'm sending you a text message with a link to book your appointment online.",
		shortURLEnabled: true,
		askForPermissionOnText: true,
		postExecutionPrompt:
			"Tell the caller you've sent them a booking link via text message and they can use it to schedule their appointment at their convenience.",
		customTextFooter: '',
	});

	const getEditFormValues = (workflow: IWorkflow) => ({
		description: workflow.description,
		textMessage: workflow.textMessage,
		voiceResponse: workflow.voiceResponse,
		shortURLEnabled: workflow.shortURLEnabled !== undefined ? workflow.shortURLEnabled : true,
		askForPermissionOnText: workflow.askForPermissionOnText,
		postExecutionPrompt: workflow.postExecutionPrompt,
		customTextFooter: workflow.customTextFooter,
	});

	return {
		existingWorkflows,
		handleSubmit,
		getDefaultFormValues,
		getEditFormValues,
	};
};
